import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  noindex?: boolean;
}

const BASE_TITLE = 'ComponentScale';
const DEFAULT_DESCRIPTION = 'Compare PC cases visually with accurate 2D and 3D size comparisons. Find the perfect case for your build with detailed specifications and measurements.';

function setMetaTag(attr: string, key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(url: string | undefined) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (url) {
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  } else if (link) {
    link.remove();
  }
}

export const useSEO = ({ title, description, image, url, type, noindex }: SEOProps) => {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${BASE_TITLE}` : BASE_TITLE;
    const desc = description || DEFAULT_DESCRIPTION;

    // Title
    document.title = fullTitle;

    // Robots
    if (noindex) {
      setMetaTag('name', 'robots', 'noindex, nofollow');
    }

    // Standard meta
    setMetaTag('name', 'description', desc);

    // Open Graph
    setMetaTag('property', 'og:title', fullTitle);
    setMetaTag('property', 'og:description', desc);
    if (type) setMetaTag('property', 'og:type', type);
    if (image) setMetaTag('property', 'og:image', image);
    if (url) setMetaTag('property', 'og:url', url);

    // Twitter Card
    setMetaTag('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    setMetaTag('name', 'twitter:title', fullTitle);
    setMetaTag('name', 'twitter:description', desc);
    if (image) setMetaTag('name', 'twitter:image', image);

    // Canonical URL
    setCanonical(url);

    // Cleanup - restore defaults when unmounting
    return () => {
      document.title = BASE_TITLE;
      setMetaTag('name', 'description', DEFAULT_DESCRIPTION);
      setMetaTag('property', 'og:title', BASE_TITLE);
      setMetaTag('property', 'og:description', DEFAULT_DESCRIPTION);
      setMetaTag('property', 'og:type', 'website');
      // Remove case-specific tags
      document.querySelector('meta[property="og:image"]')?.remove();
      document.querySelector('meta[property="og:url"]')?.remove();
      document.querySelector('meta[name="twitter:image"]')?.remove();
      document.querySelector('meta[name="robots"]')?.remove();
      setCanonical(undefined);
    };
  }, [title, description, image, url, type, noindex]);
};
